import { Tooltip } from 'antd';
import { concat } from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Icon from '@ant-design/icons';
import { useGetBlockEventsName, useGetMappedStatusEvent } from '@api/emp/queryHooks';
import {
  BlockRuleTypeOptions,
  EVENT_NAME_WITH_BLOCK_RULE,
  scaledBlockRuleTagKeyOptions,
} from '@components/block-event-single/constant/constant';
import { useBlockEventSinglePanelStore } from '@components/block-event-single/store';
import {
  getDefaultBlockEventData,
  getDefaultBlockEventRuleItem,
} from '@components/block-event-single/utils/initializeUtils';
import { isMapped } from '@components/common/AutoCompleteInput/buildOptionsWithTag';
import { MappingRulesButton } from '@components/common/AutofillButton/styled';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import { createFiltersGroup } from '@components/mapping-panel/utils/createFiltersUtils';
import MappingRulesSvg from '@icons/mapping-panel/mapping-rules-icon.svg';
import { ConvivaEvent, mappedEventDimensionType } from '@src/common/constant/enum';
import commonSelector from '@store/common/selectors';

const BlockAutoFillButton = () => {
  const { appliedFilters } = useSelector(commonSelector);
  const [
    blockEventSingleOpen,
    blockEventsSingleDataGroup,
    setBlockType,
    setBlockEventsSingleDataGroup,
  ] = useBlockEventSinglePanelStore((state) => [
    state.blockEventSingleOpen,
    state.blockEventsSingleDataGroup,
    state.setBlockType,
    state.setBlockEventsSingleDataGroup,
  ]);

  const { data: mappedEventNameData } = useGetMappedStatusEvent('event_name');
  const { data: blockEventsName } = useGetBlockEventsName();
  const { data: networkTargetUrlTagValue } = useQueryDimensionData(
    {
      fields: [mappedEventDimensionType.merged_event_tag_value],
      filters: createFiltersGroup(
        EVENT_NAME_WITH_BLOCK_RULE[0],
        scaledBlockRuleTagKeyOptions[0].value,
      ),
    },
    500,
  );
  const networkTargetUrlTagValueName = useMemo(() => {
    return (networkTargetUrlTagValue ?? []).map(
      (value) => value[mappedEventDimensionType.merged_event_tag_value],
    );
  }, [networkTargetUrlTagValue]);

  const filterRulesMap = useMemo(() => {
    const resMap: { [key: string]: any[] } = {
      [mappedEventDimensionType.all_event_name]: [],
      [mappedEventDimensionType.custom_event_name]: [],
      [mappedEventDimensionType.conviva_event_name]: [],
      [mappedEventDimensionType.merged_event_tag_value]: [],
    };
    appliedFilters[0].rule.forEach((andRuleGroup) => {
      andRuleGroup.forEach((rule) => {
        if (Object.prototype.hasOwnProperty.call(resMap, rule.field)) {
          resMap[rule.field].push(rule);
        }
      });
    });
    return resMap;
  }, [appliedFilters]);

  const eventNameSet: any[] = concat(
    filterRulesMap[mappedEventDimensionType.all_event_name],
    filterRulesMap[mappedEventDimensionType.custom_event_name],
    filterRulesMap[mappedEventDimensionType.conviva_event_name],
  );
  const tagValueSet = filterRulesMap[mappedEventDimensionType.merged_event_tag_value]?.filter(
    (item) => networkTargetUrlTagValueName.includes(item.value),
  );

  const isDisplayAutofill = blockEventSingleOpen && eventNameSet.length > 0;
  const isEventNameFilled = blockEventsSingleDataGroup.some(
    (dataGroup) => dataGroup.event_name && dataGroup.event_name.trim() !== '',
  );
  const isMappedEvent =
    !EVENT_NAME_WITH_BLOCK_RULE.includes(eventNameSet[0]?.value) &&
    isMapped(eventNameSet[0]?.value, mappedEventNameData?.data);
  const isBlockEvent = (blockEventsName ?? [])
    .filter((name) => !EVENT_NAME_WITH_BLOCK_RULE.includes(name))
    .includes(eventNameSet[0]?.value);
  const isConvivaEvent = Object.keys(ConvivaEvent)
    .filter((name) => !EVENT_NAME_WITH_BLOCK_RULE.includes(name))
    .includes(eventNameSet[0]?.value);
  const isCanAutofill =
    eventNameSet.length === 1 &&
    !isEventNameFilled &&
    !isMappedEvent &&
    !isBlockEvent &&
    !isConvivaEvent;

  const generateBlockEventFromFilter = () => {
    const res = [getDefaultBlockEventData()];
    const isEventNameWithBlockRules = EVENT_NAME_WITH_BLOCK_RULE.includes(eventNameSet?.[0]?.value);
    if (isEventNameWithBlockRules) {
      res[0].event_name = eventNameSet?.[0]?.value;
      if (tagValueSet.length > 0) {
        res[0].block_rules = [
          tagValueSet.map((item) => {
            return {
              ...getDefaultBlockEventRuleItem(),
              value: [item?.value],
            };
          }),
        ];
      }
    } else {
      res[0].event_name = eventNameSet?.[0]?.value;
    }
    return res;
  };

  const handleAutoFillClick = () => {
    if (isCanAutofill) {
      const isEventNameWithBlockRules = EVENT_NAME_WITH_BLOCK_RULE.includes(
        eventNameSet?.[0]?.value,
      );
      const blockEvents = generateBlockEventFromFilter();
      if (isEventNameWithBlockRules && blockEvents[0].block_rules.length > 0) {
        setBlockType(BlockRuleTypeOptions[1].value);
      } else {
        setBlockType(BlockRuleTypeOptions[0].value);
      }
      setBlockEventsSingleDataGroup(blockEvents);
    }
  };

  return isDisplayAutofill ? (
    <Tooltip title="">
      <MappingRulesButton disabled={!isCanAutofill} onClick={handleAutoFillClick}>
        <Icon component={MappingRulesSvg} style={{ fontSize: '20px' }} />
        <span>Block This</span>
      </MappingRulesButton>
    </Tooltip>
  ) : null;
};

export default BlockAutoFillButton;
